import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleX, Users } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthProvider";
import { Button, ButtonLink } from "../components/Button";
import { FormAlert } from "../components/fields";
import { useDocumentTitle } from "../components/layout";
import { OfflineBanner } from "../components/Offline";
import { Spinner } from "../components/Spinner";
import { ApiError, api, errorMessage, post } from "../lib/api";
import type { InvitationPreview } from "../lib/types";
import { AuthLayout } from "./auth/AuthLayout";

export function JoinPage() {
  useDocumentTitle("Join a household");
  const { code = "" } = useParams();
  const { me, refreshMe, selectHousehold } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = useQuery({
    queryKey: ["invitation", code],
    queryFn: ({ signal }) => api<InvitationPreview>(`/api/v1/invitations/${encodeURIComponent(code)}`, { signal, noCache: true }),
    // Accepting consumes the invitation. Household switching clears queries; do not re-fetch
    // the now-used preview while the successful join is navigating to the kitchen.
    enabled: !busy,
    retry: false,
  });

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await post<{ householdId: string }>(`/api/v1/invitations/${encodeURIComponent(code)}/accept`);
      await refreshMe();
      await selectHousehold(result.householdId);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] === "h" });
      toast.success(`You joined ${preview.data?.householdName ?? "the household"}`);
      navigate("/recipes", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setError("You're already a member of this household.");
      else if (err instanceof ApiError && err.status === 403) setError("This invitation is for a different email address. Sign in with the invited email to accept it.");
      else if (err instanceof ApiError && err.status === 410) setError("This invitation has expired, was revoked, or has already been used. Ask for a new one.");
      else setError(errorMessage(err));
      setBusy(false);
    }
  };

  const hasHousehold = !!me?.households.length;

  if (preview.isPending) {
    return (
      <AuthLayout title="Checking your invitation">
        <div className="auth-sent">
          <Spinner size="lg" label="Checking invitation" />
        </div>
      </AuthLayout>
    );
  }

  if (preview.isError) {
    const status = preview.error instanceof ApiError ? preview.error.status : 0;
    return (
      <AuthLayout title={status === 410 ? "This invitation can't be used" : status === 404 ? "Invitation not found" : "Couldn't open this invitation"}>
        <div className="auth-sent auth-sent--error">
          <CircleX size={44} aria-hidden="true" />
          <p>
            {status === 410
              ? "It has expired, been revoked, or already been used. Ask the person who invited you for a new one."
              : status === 404
                ? "Check the code or link — it may have been mistyped."
                : errorMessage(preview.error)}
          </p>
        </div>
        <ButtonLink to={hasHousehold ? "/recipes" : "/welcome"} emphasis="solid" intent="brand" block>
          {hasHousehold ? "Back to my kitchen" : "Set up a household instead"}
        </ButtonLink>
      </AuthLayout>
    );
  }

  const invite = preview.data;
  return (
    <>
      <OfflineBanner />
      <AuthLayout title={`Join ${invite.householdName}`}>
        <div className="invite-card">
          <Users size={28} aria-hidden="true" />
          <div>
            <p>
              You've been invited to join <strong>{invite.householdName}</strong> as {invite.role === "admin" ? "an admin" : "a member"}.
            </p>
            <p className="muted">
              Expires {new Date(invite.expiresAt).toLocaleDateString("en-US", { dateStyle: "medium" })}
              {invite.restrictedToEmail ? " · for a specific email address" : ""}
            </p>
          </div>
        </div>
        <FormAlert>{error}</FormAlert>
        {invite.alreadyMember ? (
          <>
            <FormAlert tone="info">You're already a member of this household.</FormAlert>
            <ButtonLink to="/recipes" emphasis="solid" intent="brand" block>
              Go to my kitchen
            </ButtonLink>
          </>
        ) : (
          <div className="stack stack--sm">
            <Button emphasis="solid" intent="brand" size="lg" block busy={busy} write onClick={() => void accept()}>
              Join household
            </Button>
            <ButtonLink to={hasHousehold ? "/recipes" : "/welcome"} emphasis="ghost" intent="neutral" block>
              Not now
            </ButtonLink>
          </div>
        )}
      </AuthLayout>
    </>
  );
}
